import type { AppContext, EntityInView } from './types';
import { collapse, truncate } from './util';
import { log } from './logger';

let entitySelector = '[data-entity]';
let appMeta: Record<string, string> = {};
let onRouteChange: (() => void) | null = null;
let lastRoute = '';
let patched = false;

export function configureContext(options: {
  entitySelector: string;
  appMeta: Record<string, string>;
}): void {
  entitySelector = options.entitySelector;
  appMeta = options.appMeta;
}

function readEntityInView(): EntityInView | null {
  let node: Element | null = null;
  try {
    node = document.querySelector(entitySelector);
  } catch {
    log.warn(`entitySelector "${entitySelector}" is not a valid selector`);
    return null;
  }
  if (!node) return null;

  const type = collapse(node.getAttribute('data-entity')) || 'unknown';
  const id = collapse(node.getAttribute('data-entity-id'));
  const name =
    collapse(node.getAttribute('data-entity-name')) ||
    collapse(node.querySelector('h1')?.textContent) ||
    '';

  return { type, id, name: truncate(name, 120) };
}

export function readContext(): AppContext {
  return {
    route: window.location.pathname + window.location.search,
    title: truncate(collapse(document.title), 160),
    app_meta: appMeta,
    entity_in_view: readEntityInView(),
  };
}

function notifyIfRouteChanged(): void {
  const route = window.location.pathname + window.location.search;
  if (route === lastRoute) return;
  lastRoute = route;
  onRouteChange?.();
}

export function watchRoute(handler: () => void): void {
  onRouteChange = handler;
  lastRoute = window.location.pathname + window.location.search;

  window.addEventListener('popstate', notifyIfRouteChanged);
  window.addEventListener('hashchange', notifyIfRouteChanged);

  if (patched) return;
  patched = true;

  const history = window.history;
  const originalPush = history.pushState.bind(history);
  const originalReplace = history.replaceState.bind(history);

  history.pushState = function pushState(...args: Parameters<History['pushState']>) {
    originalPush(...args);
    notifyIfRouteChanged();
  };
  history.replaceState = function replaceState(...args: Parameters<History['replaceState']>) {
    originalReplace(...args);
    notifyIfRouteChanged();
  };
}

export function stopWatchingRoute(): void {
  onRouteChange = null;
  window.removeEventListener('popstate', notifyIfRouteChanged);
  window.removeEventListener('hashchange', notifyIfRouteChanged);
}
